/**
 * Trust score: cuántos tratos hemos completado con cada amigo.
 *
 * Fuente: state.history (cada entry tipo 'trade' incluye friendUid
 * desde el commit que lo agregó). Entradas viejas sin friendUid se
 * ignoran — no podemos atribuirlas con certeza.
 *
 * Objetivo UX: poner un badge "✅ N tratos" al lado del nombre del
 * amigo en lista de Amigos y en cards de Propuestas. Construye
 * confianza social — al usuario le interesa más comerciar con
 * alguien con 12 tratos completados que con alguien sin historial.
 *
 * Performance: re-construimos el contador desde history cada vez
 * que cambia. history está truncado a 10 entries en
 * applyCompletedTrade, así que el conteo es O(10) — fine sin memo.
 * Si crecemos history a 100+ en el futuro, agregar useMemo en el
 * call site.
 */

/**
 * @param {Array} history  state.history (puede ser null/undefined)
 * @returns {Object} { friendUid: count } — cuántos tratos completados con cada amigo
 */
export function computeTrustScores(history) {
  const counts = {};
  if (!Array.isArray(history)) return counts;
  history.forEach((h) => {
    if (!h || h.type !== 'trade') return;
    if (!h.friendUid) return;  // entradas pre-trust-score (sin uid) se ignoran
    counts[h.friendUid] = (counts[h.friendUid] || 0) + 1;
  });
  return counts;
}

/**
 * Devuelve el count de tratos con un amigo específico (0 si no hay
 * historial). Atajo para call-sites que solo necesitan uno.
 */
export function getTrustScore(history, friendUid) {
  if (!friendUid) return 0;
  const all = computeTrustScores(history);
  return all[friendUid] || 0;
}

/**
 * Etiqueta humana para el trust score. Útil para tooltips/badges.
 *   0 trades  → "Sin tratos previos"
 *   1 trade   → "1 trato completado"
 *   N trades  → "N tratos completados"
 */
export function trustLabel(count) {
  if (!count || count <= 0) return 'Sin tratos previos';
  if (count === 1) return '1 trato completado';
  return `${count} tratos completados`;
}
